import {Message} from 'com/gongxigames/math/core/game/message/Message';
import {Field} from 'com/gongxigames/math/core/game/message/Field';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {
    GameClientWrapper,
    Xhr,
    WrapperUtils,
    LogicEvents,
    WaitingModule,
    KeypadControlsEvents,
    GameClientWrapperProperties,
    FlowModuleEvents,
    GameConfig,
    CancelStartSpinReason,
    LOW_BALANCE,
    CancelStartSpinData
} from '@gongxigames/framework/slot';
import {RubyPlayErrorsHandler, GameServerError} from '@gongxigames/framework/integrations/rubyplay/RubyPlayErrorsHandler';
import {HTTP_CLIENT_WRAPPER_SETTINGS_PARAMETERS_SCHEMA, RubyPlayGameClientWrapperSettings} from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapperSettings';
import {ClientSideMessages, RubyPlayPostMessageHandler} from '@gongxigames/framework/integrations/rubyplay/RubyPlayPostMessageHandler';
import {FunModeHandler, IModHandler, NullModeHandler} from '@gongxigames/framework/integrations/rubyplay/modHandler';
import {
    BetsConfig,
    PlayerConfig,
    PROMOTION_DECISION_ACTION,
    PromotionDecision,
    PromotionDecisionRequest,
    ResponseBody,
    ServerResponseData
} from '@gongxigames/framework/integrations/rubyplay/types';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {LockManager, System, inject} from '@gongxigames/framework/core';
import {MultiSetsMulticampaignPromotion, PromotionBalanceExtractor, IPromotionLogicInterface} from '@gongxigames/framework/promotions';
import {strictCoreEmitter} from '@gongxigames/framework/core/globals';

const FIELD_DEVICE_TYPE = 'device_type';
const FIELD_AUTOPLAY_ENABLED = 'autoplay_enabled';
const ODDS_UPFRONT_ACCEPTANCE_PATH = '/game-rules/odds';
const TWELVE_MINUTES = 720000;
const DEFAULT_POST_HEADERS = {'Content-Type': 'application/json'};
const pingBalanceLockKey = 'pingBalanceRequest';

type BalanceRequestBlockingReason = 'game_freezed' | 'game_invisible' | 'game_paused' | 'round_started';

export class RubyPlayGameClientWrapper extends GameClientWrapper {
    @inject(CoreDI.System)
    protected _system!: System;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    public static NAME = 'rubyplay';
    // 10 minutes
    public static readonly SLOW_REQUEST_TIMEOUT: number = 600000;
    public static readonly PING_BALANCE_ATTEMPTS: number = 5;

    protected _initSessionUrl!: string;
    protected _serverUrl!: string;
    protected _gameServerUrl!: string;
    protected _playerSession!: string;
    protected _gameName!: string;
    protected _operator!: string;
    protected _key!: string;
    protected _timeoutID!: number;
    protected _pingBalanceTimeoutID?: number;
    protected _errors!: RubyPlayErrorsHandler;
    protected _defaultBet!: number;
    protected _defaultPromotionBet!: number;
    protected _deviceType!: string;
    protected _pingBalanceTryCounter: number = RubyPlayGameClientWrapper.PING_BALANCE_ATTEMPTS;
    // default bet that player had before started FR in order to restore it after FR finished
    protected _betInRegularGame!: number;
    protected _postMessageHandler!: RubyPlayPostMessageHandler;
    protected _pingBalanceRequest?: XMLHttpRequest;
    protected _isPingBalanceSuspended?: boolean;
    protected _oddsUpfrontAcceptanceUrl!: string;
    protected _promotionLogicInterface: IPromotionLogicInterface = new MultiSetsMulticampaignPromotion();
    protected _modeHandler!: IModHandler;
    protected _lockManager = new LockManager();
    protected isRefreshedBeforeSpin = false

    public initWrapper(properties: GameClientWrapperProperties): void {
        this.createModeHandler();
        this.initLockManager();
        super.initWrapper(properties);
    }

    protected initLockManager(): void {
        this._lockManager.add(pingBalanceLockKey, this.onPingBalanceLockUpdate.bind(this));
    }

    protected createModeHandler(): void {
        const settings = this.getSettings<RubyPlayGameClientWrapperSettings>();

        this._modeHandler = this.isFunMode() ?
            new FunModeHandler(settings) :
            new NullModeHandler();
    }

    protected setupEvents(): void {
        super.setupEvents();
        this._eventManager.on('General.Balance.REFRESH', this.refreshBalanceRequest.bind(this), this);
        this._eventManager.on('General.Game.ODDS_UPFRONT_ACCEPT', this.updateOddsAcceptance.bind(this), this);
        this._eventManager.on('General.Promotion.Started', this.onPromotionStarted.bind(this), this);
        this._eventManager.on('General.Screen.BECAME_INVISIBLE', this.cancelPingBalance.bind(this), this);
        this._eventManager.on('General.Screen.BECAME_VISIBLE', this.addPingBalanceTimeout.bind(this), this);
        this._eventManager.on('General.System.PAUSE', this.onPause.bind(this), this);
        this._eventManager.onAsync('General.System.PAUSE', this.onPause.bind(this), this);
        this._eventManager.on('General.System.RESUME', this.onResume.bind(this), this);
        this._eventManager.onAsync('General.System.RESUME', this.onResume.bind(this), this);
        this._eventManager.on('General.System.FREEZE', this.onFreeze.bind(this), this);
        this._eventManager.on(LogicEvents.ROUND_STARTED, this.onRoundStarted.bind(this), this);
        this._eventManager.on(FlowModuleEvents.FEATURE_STARTED, this.onRoundStarted.bind(this), this);
        this._eventManager.on('General.Flow.PAID_ROUND_ENDED', this.onPaidRoundEnded.bind(this), this);
    }

    protected onCancelStartSpin({reason, balanceRefreshCallback}: CancelStartSpinData): void {
        if (reason === CancelStartSpinReason.INSUFFICIENT_FUNDS) {
            if (this.isFunMode()) {
                this._eventManager.emit('General.Message.SHOW', LOW_BALANCE);

                return;
            }

            if (this.isRefreshedBeforeSpin) {
                this.isRefreshedBeforeSpin = false;
                this.hideInsufficientFundsWaitingPopUp();
                this._eventManager.emit('General.Message.SHOW', LOW_BALANCE);
            } else {
                this._eventManager.on(LogicEvents.BALANCE_UPDATED, () => {
                    this.isRefreshedBeforeSpin = true;
                    this.hideInsufficientFundsWaitingPopUp();
                    balanceRefreshCallback?.();
                    this._eventManager.off(LogicEvents.BALANCE_UPDATED, this);
                }, this);

                this._eventManager.emit('General.UI.DISABLE', CancelStartSpinReason.INSUFFICIENT_FUNDS);
                this._eventManager.emit('General.Balance.REFRESH');
                this._eventManager.emit(WaitingModule.SHOW_POPUP);
            }
        }
    }

    protected hideInsufficientFundsWaitingPopUp(): void {
        this._eventManager.emit('General.UI.ENABLE', CancelStartSpinReason.INSUFFICIENT_FUNDS);
        this._eventManager.emit(WaitingModule.HIDE_POPUP);
    }

    public getDefaultBet(): number {
        return this.getPromotion().isActive() ? this.getPromotion().getBet() : this._defaultBet;
    }

    public updateDefaultBet(): void {
        if (this.getPromotion().isActive()) {
            this._defaultBet = this.getPromotion().getBet();
        } else {
            // if game restored on FR then betInRegularGame was not assigned and we need to keep with the standard defaultBet
            this._defaultBet = this._betInRegularGame || this._defaultBet;
        }

        // TODO: add logic event (BETCHANGED)
        this._eventManager.emit(KeypadControlsEvents.UPDATE_KEYPAD);
    }

    public sendPromotionDecision(decision: PromotionDecision, succeessCB: () => void, failureCB: () => void = () => {}): void {
        const payload: PromotionDecisionRequest = {
            key: this._key,
            action: PROMOTION_DECISION_ACTION,
            an: this.getSession().getActionNumber(),
            promotion_decision: decision,
            promotion_id: this.getPromotion().getID()
        };
        const payloadStr = JSON.stringify(payload);

        Xhr.post(this._gameServerUrl, payloadStr, (body: ResponseBody) => {
            if (body.status === 'ok') {
                this._betInRegularGame = this._defaultBet;

                succeessCB();
            } else {
                this._errors.handleGameServerInfo(body, failureCB);
            }
        }, DEFAULT_POST_HEADERS, this.onErrorRequest.bind(this));
    }

    protected initParameters(): void {
        super.initParameters();

        Xhr.CONNECTION_TIMEOUT = TWELVE_MINUTES;

        const settings = this.getSettings<RubyPlayGameClientWrapperSettings>();

        settings.addParametersSchema(HTTP_CLIENT_WRAPPER_SETTINGS_PARAMETERS_SCHEMA);

        const {
            serverUrl,
            initUrl,
            gsUrl,
            key,
            gameName,
            operator,
            playerSession,
            lang,
            platform
        } = this.getSettings<RubyPlayGameClientWrapperSettings>().getAllProperties();

        this._initSessionUrl = initUrl || `${serverUrl}/init-session`;
        this._serverUrl = gsUrl || `${serverUrl}`;
        this._gameServerUrl = gsUrl || `${serverUrl}/gameserver`;
        this._playerSession = playerSession;
        this._gameName = gameName;
        this._operator = operator;
        this._key = key;
        this._postMessageHandler = new RubyPlayPostMessageHandler(this._eventManager, this.emitter, this._browser, gameName, lang);
        this._errors = this.createErrorsHandler();
        this._oddsUpfrontAcceptanceUrl = serverUrl + ODDS_UPFRONT_ACCEPTANCE_PATH;
        this._deviceType = platform;

        this._initSessionUrl = this._modeHandler.extendUrl(this._initSessionUrl);
        this._gameServerUrl = this._modeHandler.extendUrl(this._gameServerUrl);
        this._oddsUpfrontAcceptanceUrl = this._modeHandler.extendUrl(this._oddsUpfrontAcceptanceUrl);
    }

    protected updateOddsAcceptance(): void {
        // TODO use only one event (new 'CORE.waiting.show')
        this._eventManager.emit(WaitingModule.SHOW_POPUP);
        strictCoreEmitter.emit('CORE.waiting.show');

        const payload = {
            ...this._modeHandler.getOddsAcceptancePayload(),
            sessionKey: this._key
        };
        const payloadStr = JSON.stringify(payload);

        Xhr.post(this._oddsUpfrontAcceptanceUrl, payloadStr, () => {
            this._eventManager.emit(WaitingModule.HIDE_POPUP);
            strictCoreEmitter.emit('CORE.waiting.hide');
            this._eventManager.emit('General.Game.ODDS_UPFRONT_ACCEPTED');
        }, {'content-type': 'application/json'}, this.onErrorOddAcceptance.bind(this));
    }

    protected onPromotionStarted(): void {
        this.addPingBalanceTimeout();
    }

    protected onErrorOddAcceptance(): void {
        this._eventManager.emit(WaitingModule.HIDE_POPUP);
        strictCoreEmitter.emit('CORE.waiting.hide');
        this.onErrorRequest();
    }

    protected sendToServer(request: Message): void {
        if (request.getAction() === Action.INIT) {
            if (this._key) {
                // proceed to init if key is already defined
                this.gameServerActionRequest(request);
            } else {
                // init session to get key before calls to gameserver
                this.initSession(request);
            }
        } else {
            const testSession: number[] = request.getField(Field.TEST_SESSION);

            if (testSession !== null) {
                request.addField(Field.TEST_SESSION, WrapperUtils.bytesToBase64(testSession));
            }

            this.gameServerActionRequest(request);
        }
    }

    protected initSession(request: Message): void {
        const {stId, stName} = this.getSettings<RubyPlayGameClientWrapperSettings>().getAllProperties();
        const {clientVersion} = this._gameConfig;
        const fields = {
            playerSession: this._playerSession,
            gamename: this._gameName,
            operator: this._operator,
            ...(stId && {stId}),
            ...(stName && {stName}),
            ...this._modeHandler.getInitSessionRequestPayload(clientVersion)
        };

        Xhr.get(this._initSessionUrl, (body: ResponseBody) => {
            this.onInitSessionResponse(body, request);
        }, null, fields, this.onErrorRequest.bind(this));

        this.addConnectionTimeout();
    }

    protected onInitSessionResponse(body: ResponseBody, request: Message): void {
        this.clearConnectionTimeout();

        const {status, sessionKey} = body;

        if (status === 'success' && sessionKey) {
            this._modeHandler.handleInitSessionResponse(body);
            this.onInitSessionResponseSuccess(sessionKey, request);
        } else {
            this.onInitSessionResponseError(body);
        }
    }

    protected onInitSessionResponseSuccess(sessionKey: string, request: Message): void {
        this._errors.closeSlowConnection();
        this.addSessionKey(sessionKey);
        this.gameServerActionRequest(request);
    }

    protected onInitSessionResponseError(body: ResponseBody): void {
        this._errors.handleInitSessionError(body);
    }

    protected addSessionKey(sessionKey: string): void {
        this._key = sessionKey;
    }

    protected gameServerActionRequest(request: Message): void {
        const action = request.getAction();
        const payload = this.createPayloadForServerPostRequest(request);
        const payloadStr = JSON.stringify(payload);

        Xhr.post(this._gameServerUrl, payloadStr, (body: ResponseBody) => {
            this.clearConnectionTimeout();

            if (body.status === 'ok') {
                this._modeHandler.handleActionResponse(body);
                this.onGameServerActionResponse(body, action);
            } else {
                this.onGameServerActionError(body, request);
            }
        }, DEFAULT_POST_HEADERS, this.onErrorRequest.bind(this));

        this.cancelPingBalance();
        this.addConnectionTimeout();
    }

    protected createPayloadForServerPostRequest(request: Message): Message {
        const action = request.getAction();
        const promotionLogicInterface = this.getPromotion<IPromotionLogicInterface>();
        const promotionAdditionRequestFields = promotionLogicInterface.getAdditionalFieldsForServerRequest(action);

        request
            .addField(Field.KEY, this._key)
            .addField(FIELD_DEVICE_TYPE, this._deviceType);

        if (promotionAdditionRequestFields) {
            Object.keys(promotionAdditionRequestFields).forEach(key => {
                request.addField(key, promotionAdditionRequestFields[key]);
            });
        }

        // we should inform server about any paid spin with enabled autoplay
        if (this.getSettings<RubyPlayGameClientWrapperSettings>().get('autoplayMode') && action === Action.SPIN) {
            request.addField(FIELD_AUTOPLAY_ENABLED, true);
        }

        return {
            ...request.getFields(),
            ...this._modeHandler.getActionRequestPayload(),
            action: WrapperUtils.getActionName(action)
        };
    }

    protected onGameServerActionResponse(body: ResponseBody, action: Action): void {
        this._errors.closeSlowConnection();
        const response = WrapperUtils.parseMessage(body, action);

        this.onServerResponse(response);

        if (this.isInitAction(action)) {
            this.parseInitResponse(body);
        }

        this.handlePromotion(body, action);
        this.addPingBalanceTimeout();
    }

    protected handlePromotion(body: ResponseBody, action?: Action): void {
        const promotionLogicInterface = this.getPromotion<IPromotionLogicInterface>();

        if (this.shouldInitPromotion(body, action)) {
            promotionLogicInterface.init({
                initResponse: body,
                clientWrapper: this,
                defaultBet: this._defaultPromotionBet
            });
        } else {
            promotionLogicInterface.update(body);
        }
    }

    protected shouldInitPromotion(body: ResponseBody, action?: Action): boolean {
        return this.isInitAction(action) || this.receivedNewPromotion(body) || this.receivedUsedPromotion(body);
    }

    protected receivedNewPromotion(body: ResponseBody): boolean {
        return PromotionBalanceExtractor.hasNewPromotion(body.data.player);
    }

    protected receivedUsedPromotion(body: ResponseBody): boolean {
        const hasPromotion = this.getPromotion<IPromotionLogicInterface>().hasPromotion();
        const gotUsedPromotion = PromotionBalanceExtractor.hasUsedPromotion(body.data.player);

        return !hasPromotion && gotUsedPromotion;
    }

    protected onGameServerActionError(body: GameServerError, request: Message): void {
        this._errors.handleGameServerError(body);
    }

    protected onErrorRequest(xhr?: XMLHttpRequest, isTimeout?: boolean): void {
        this.clearConnectionTimeout();
        this._errors.handleRequestError(xhr!, isTimeout!);
    }

    private startPingBalanceRequests(): void {
        const payload: any = {
            key: this._key,
            action: 'balance',
            an: this.getSession().getActionNumber()
        };

        const ping = () => {
            if (this._pingBalanceTryCounter > 0 && !this._errors.isSessionExpired()) {
                this._pingBalanceTryCounter--;
                this.addPingBalanceTimeout();
            }
        };

        if (this.addIgnoreCacheParameterToRequest()) {
            payload.ignoreCache = true;
        }

        const payloadStr = JSON.stringify(payload);

        this._pingBalanceRequest = Xhr.post(this._gameServerUrl, payloadStr, (body: ResponseBody) => {
            const {status} = body;

            this._pingBalanceRequest = undefined;

            if (status === 'ok') {
                this._errors.closeSlowConnection();
                this._pingBalanceTryCounter = RubyPlayGameClientWrapper.PING_BALANCE_ATTEMPTS;
                this.updateBalance(body);
            } else {
                this._errors.handleGameServerError(body);
                this.hideInsufficientFundsWaitingPopUp();
            }

            ping();
        }, DEFAULT_POST_HEADERS, (xhr: XMLHttpRequest) => {
            if (this._internetConnectionLost) {
                this._pingBalanceTryCounter++;
            } else {
                this._pingBalanceRequest = undefined;
                this.onErrorRequest(xhr);
                this.hideInsufficientFundsWaitingPopUp();
            }

            ping();
        });
    }

    protected pingBalance(): void {
        if (this._pingBalanceRequest && this._pingBalanceRequest.readyState !== XMLHttpRequest.DONE) {
            return;
        }

        if (this.isFunMode()) {
            return;
        }

        this.startPingBalanceRequests();
    }

    protected onPingBalanceLockUpdate(lock: boolean): void {
        if (!lock && this._isPingBalanceSuspended) {
            this._isPingBalanceSuspended = false;
            this.pingBalance();
        }
    }

    private requestPingBalance(): void {
        if (this.canPingForBalance()) {
            this.pingBalance();
        } else {
            this._isPingBalanceSuspended = true;
        }
    }

    protected lockPingBalance(reason: BalanceRequestBlockingReason): void {
        this._lockManager.lock(pingBalanceLockKey, reason);
    }

    protected unlockPingBalance(reason: BalanceRequestBlockingReason): void {
        this._lockManager.unlock(pingBalanceLockKey, reason);
    }

    protected onRoundStarted(): void {
        this.lockPingBalance('round_started');
    }

    protected onPaidRoundEnded(): void {
        this.unlockPingBalance('round_started');
    }

    protected onBecameInvisible(): void {
        this.lockPingBalance('game_invisible');
    }

    protected onBecameVisible(): void {
        this.unlockPingBalance('game_invisible');
    }

    protected onPause(): void {
        this.lockPingBalance('game_paused');
    }

    protected onResume(): void {
        this.unlockPingBalance('game_paused');
    }

    protected onFreeze(): void {
        this.lockPingBalance('game_freezed');
    }

    private canPingForBalance(): boolean {
        return !this._lockManager.getLock(pingBalanceLockKey).isLocked;
    }

    protected addIgnoreCacheParameterToRequest(): boolean {
        return this.getSettings<RubyPlayGameClientWrapperSettings>().get('pingBalanceIgnoreCache');
    }

    protected updateBalance(body: ResponseBody): void {
        const balance = Number(body.data.player.balance);

        this.getSession().getPlayer().setBalance(balance);

        this.handlePromotion(body);

        this._eventManager.emit(LogicEvents.BALANCE_UPDATED, balance);
    }

    protected redirectToHomeUrl(): void {
        this._eventManager.emit(ClientSideMessages.QUIT_TO_LOBBY);
        super.redirectToHomeUrl();
    }

    protected getBetsConfig(data: ServerResponseData): BetsConfig {
        const {game_config} = data;

        if (!game_config || !game_config.bets || game_config.def_bet_index === undefined) {
            throw new Error('Malformed game configuration: ' + JSON.stringify(game_config, undefined, 3));
        }

        return game_config;
    }

    protected getPlayerConfig(data: ServerResponseData): PlayerConfig {
        const {player} = data;

        if (!player || !player.currency || !player.subunit) {
            throw new Error('Malformed player configuration: ' + JSON.stringify(player, undefined, 3));
        }

        return player;
    }

    protected onInitResponse(data: ServerResponseData): void {
        const {regulation_settings, buy_feature_available, promotion_settings} = data;
        const {currency, subunit} = this.getPlayerConfig(data);
        const {bets, def_bet_index, max_bf_bet} = this.getBetsConfig(data);
        const defaultFreeSpinInx = data.game_config?.def_free_spin_index;
        const settings = this.getSettings<RubyPlayGameClientWrapperSettings>();

        settings.setIfPossible('currency', currency);
        settings.setIfPossible('subunit', subunit);
        settings.setIfPossible('buyFeatureAvailable', buy_feature_available);
        settings.setIfPossible('maxBuyFeatureBet', max_bf_bet);

        this._defaultBet = bets[def_bet_index];

        if (typeof defaultFreeSpinInx === 'number') {
            this._defaultPromotionBet = bets[defaultFreeSpinInx];
        }

        if (regulation_settings) {
            this.setRegulationSettings(regulation_settings);
        }

        if (promotion_settings) {
            this.setPromotionSettings(promotion_settings);
        }

        this.configureCurrencyFormatter(data);
    }

    protected getGameHistoryListUrl(): string {
        const settings = this.getSettings<RubyPlayGameClientWrapperSettings>();
        const gamename = settings.get('gameName');
        const lang = settings.get('lang');
        const currency = settings.get('currency');
        const subunit = settings.get('subunit');

        return `${window.RP_CONFIG.game_history_list}?server_url=${this._serverUrl}&sessionKey=${this._key}` +
            `&gameId=${gamename}&lang=${lang}&currency=${currency}&subunit=${subunit}`;
    }

    protected addConnectionTimeout(): void {
        this._timeoutID = this._browser.addTimeout(this._errors.handleSlowConnection.bind(this._errors), RubyPlayGameClientWrapper.SLOW_REQUEST_TIMEOUT);
    }

    protected clearConnectionTimeout(): void {
        this._browser.removeTimeout(this._timeoutID);
    }

    private addPingBalanceTimeout(): void {
        if (this._pingBalanceTimeoutID !== undefined) {
            this.clearPingBalanceTimeout();
        }

        this._pingBalanceTimeoutID = this._browser.addTimeout(this.requestPingBalance.bind(this), this.getPingBalanceDelay());
    }

    protected getPingBalanceDelay(): number {
        const promotion = this.getPromotion();

        if (promotion.isActive() && promotion.getPingBalanceDelay) {
            return promotion.getPingBalanceDelay();
        }

        return this.getSettings<RubyPlayGameClientWrapperSettings>().get('pingBalanceDelay');
    }

    protected createErrorsHandler<E extends RubyPlayErrorsHandler>(): E | RubyPlayErrorsHandler {
        return new RubyPlayErrorsHandler(this._eventManager);
    }

    protected getErrorsHandler<E extends RubyPlayErrorsHandler>(): E {
        return this._errors as E;
    }

    private parseInitResponse(body: ResponseBody): void {
        try {
            this.onInitResponse(body.data);
        } catch (e) {
            this.onException('Unable to parse configuration:' + e);
        }
    }

    public refreshBalanceRequest(): void {
        this.cancelPingBalance();
        this.pingBalance();
    }

    protected clearPingBalanceTimeout(): void {
        if (this._pingBalanceTimeoutID) {
            this._browser.removeTimeout(this._pingBalanceTimeoutID);

            delete this._pingBalanceTimeoutID;

            this._pingBalanceTimeoutID = undefined;
        }
    }

    protected cancelPingBalance(): void {
        this.clearPingBalanceTimeout();

        if (this._pingBalanceRequest) {
            this._pingBalanceRequest.abort();
            delete this._pingBalanceRequest;
            this._pingBalanceRequest = undefined;
        }

        this._pingBalanceTryCounter = RubyPlayGameClientWrapper.PING_BALANCE_ATTEMPTS;
    }

    protected configureCurrencyFormatter(data: ServerResponseData): void {
        const settings = this.getSettings<RubyPlayGameClientWrapperSettings>();
        const currencyCode = settings.get('currency');
        const fractionSize = settings.get('subunit');
        const localeCode = settings.get('lang');
        const {currency_configuration} = data;

        if (currency_configuration) {
            this._currencyFormatter.setCurrencyData(currencyCode, currency_configuration);
        }

        this._currencyFormatter.setParameters({
            currencyCode,
            fractionSize,
            localeCode
        });
    }

    private isFunMode(): boolean {
        return this
            .getSettings<RubyPlayGameClientWrapperSettings>()
            .get('funMode');
    }
}
